import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { HRPromoCard } from '../../components/HRPromoBanner';
import beds24Logo from '../../assets/images/partners/beds24.jpeg';
import siteminderLogo from '../../assets/images/partners/siteminder.jpeg';
import littleHotelierLogo from '../../assets/images/partners/little-hotelier.jpeg';
import guestyLogo from '../../assets/images/partners/guesty.jpeg';
import {
  ArrowRight, CheckCircle2, Plug, Zap, Clock, Users,
  FileText, Shield, BarChart3, Receipt, Scale, PieChart,
  Link2, Settings, Headphones, BookOpen, CalendarDays,
  CreditCard, UserCheck, HelpCircle, ChevronRight
} from 'lucide-react';

/* ─── scroll-reveal ─── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('revealed'); obs.unobserve(el); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal-el ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function SectionTag({ children }) {
  return (
    <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-[#2563EB] text-sm font-medium mb-4">
      {children}
    </span>
  );
}

const Features = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="features-page">
      <SEO
        title="Features — Hotel e-Invoice System, PMS Integration & LHDN Compliance"
        description="StayMii features: automated LHDN e-invoicing, PMS integration (Beds24, Guesty), tax compliance dashboard, and financial reporting for Malaysian hotels, Airbnb & short-term rentals."
        keywords="hotel e invoice system, pms e invoice integration, hotel management system malaysia, lhdn e invoice features, e invoice plugin malaysia, hotel compliance platform, myinvois integration features"
        canonicalUrl="/features"
      />

      <style>{`
        .reveal-el { opacity:0; transform:translateY(24px); transition: opacity .6s cubic-bezier(.16,1,.3,1), transform .6s cubic-bezier(.16,1,.3,1); }
        .reveal-el.revealed { opacity:1; transform:translateY(0); }
      `}</style>

      <LandingNavBar />

      {/* ═══════ HERO / PAGE HEADER ═══════ */}
      <section className="pt-32 pb-20 bg-[#0B1120] relative overflow-hidden" data-testid="features-hero">
        <div className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-blue-600/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-indigo-500/6 rounded-full blur-[100px]" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-300 text-sm font-medium mb-8">
              <Settings className="w-4 h-4" /> Integration + Setup + Compliance
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Hotel e-Invoice System
              <span className="block text-[#60A5FA] mt-2">PMS Integration & LHDN Compliance</span>
            </h1>
            <p className="text-white/70 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
              Automate LHDN e-invoicing for your hotel with StayMii. Integrates with your existing PMS or channel manager — Beds24, Guesty & more. No system change required.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════ SECTION: FLEXIBLE SETUP ═══════ */}
      <section className="py-20 lg:py-28 bg-white" data-testid="flexible-setup-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <SectionTag><Plug className="h-4 w-4" /> Flexible Setup</SectionTag>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                PMS e-Invoice Integration — Use Your Current System
              </h2>
              <p className="text-slate-500 text-base">
                StayMii integrates with your PMS. No system change required.
              </p>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Option A: Existing PMS */}
            <Reveal delay={80}>
              <div className="bg-white p-8 lg:p-10 rounded-2xl border-2 border-[#2563EB]/20 hover:border-[#2563EB]/40 transition-all h-full relative" data-testid="option-existing-pms">
                <div className="absolute -top-3.5 left-8">
                  <span className="px-4 py-1.5 bg-[#2563EB] text-white text-xs font-bold rounded-full uppercase tracking-wider">
                    Option A
                  </span>
                </div>
                <div className="w-14 h-14 bg-[#2563EB] rounded-xl flex items-center justify-center mb-6 mt-2">
                  <Plug className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Already Have a PMS?</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">
                  StayMii integrates with widely used hospitality systems such as Beds24, Little Hotelier, and Guesty.
                </p>
                <ul className="space-y-3">
                  {[
                    { icon: CheckCircle2, text: 'No migration required' },
                    { icon: Zap, text: 'No disruption to operations' },
                    { icon: Clock, text: 'Go live quickly' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-slate-700 font-medium text-sm">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Option B: No PMS */}
            <Reveal delay={160}>
              <div className="bg-white p-8 lg:p-10 rounded-2xl border-2 border-slate-200 hover:border-slate-300 transition-all h-full relative" data-testid="option-no-pms">
                <div className="absolute -top-3.5 left-8">
                  <span className="px-4 py-1.5 bg-slate-700 text-white text-xs font-bold rounded-full uppercase tracking-wider">
                    Option B
                  </span>
                </div>
                <div className="w-14 h-14 bg-slate-700 rounded-xl flex items-center justify-center mb-6 mt-2">
                  <Headphones className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">No PMS Yet?</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">
                  We help you select, set up, and configure the right PMS or channel manager for your property.
                </p>
                <ul className="space-y-3">
                  {[
                    { icon: HelpCircle, text: 'System recommendation' },
                    { icon: Settings, text: 'Setup & onboarding' },
                    { icon: BookOpen, text: 'Property configuration' },
                    { icon: Link2, text: 'Integration with StayMii' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-[#2563EB]" />
                      </div>
                      <span className="text-slate-700 font-medium text-sm">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ SECTION: STAYMII CORE — COMPLIANCE & E-INVOICING (VISUALLY DOMINANT) ═══════ */}
      <section className="py-20 lg:py-28 bg-[#0B1120] relative overflow-hidden" data-testid="core-features-section">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[100px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-300 text-sm font-medium mb-4">
                <Shield className="h-4 w-4" /> Core Product
              </span>
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
                LHDN e-Invoice Automation & Tax Compliance
              </h2>
              <p className="text-white/60 text-base lg:text-lg">
                Automate your LHDN MyInvois e-invoicing, SST reporting, and financial compliance for Malaysian hotels and short-term rentals.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* E-Invoicing */}
            <Reveal delay={80}>
              <div className="bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:border-blue-500/30 transition-all h-full">
                <div className="w-14 h-14 bg-[#2563EB] rounded-xl flex items-center justify-center mb-6">
                  <Receipt className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">E-Invoicing</h3>
                <p className="text-white/60 text-sm mb-6 leading-relaxed">
                  Generate, validate, and submit LHDN-compliant e-invoices automatically from your booking and payment data.
                </p>
                <ul className="space-y-3">
                  {[
                    'E-invoicing aligned with LHDN requirements',
                    'Automated invoice generation',
                    'Submission to MyInvois tax system',
                    'Real-time validation & status tracking',
                    'Batch processing for high-volume properties',
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                      <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Tax & Compliance */}
            <Reveal delay={160}>
              <div className="bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:border-blue-500/30 transition-all h-full">
                <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center mb-6">
                  <Scale className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Tax & Compliance</h3>
                <p className="text-white/60 text-sm mb-6 leading-relaxed">
                  Stay ahead of regulatory requirements with automated SST reporting and a centralised compliance view.
                </p>
                <ul className="space-y-3">
                  {[
                    'SST reporting',
                    'Audit-ready records',
                    'Centralised compliance dashboard',
                    'Tax exemption handling',
                    'Regulatory update tracking',
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {/* Financial Dashboard — full width */}
          <Reveal delay={200}>
            <div className="bg-white/5 backdrop-blur-sm p-8 lg:p-10 rounded-2xl border border-white/10 hover:border-blue-500/30 transition-all">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center mb-6">
                    <PieChart className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Financial & Audit Reporting Dashboard</h3>
                  <p className="text-white/60 text-sm leading-relaxed mb-6">
                    Get full visibility into revenue, charges, and payments. Generate owner-ready summaries and export audit-ready reports.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: BarChart3, label: 'Revenue tracking' },
                    { icon: CalendarDays, label: 'Daily / monthly reports' },
                    { icon: Users, label: 'Owner-ready summaries' },
                    { icon: FileText, label: 'Export (PDF / Excel)' },
                  ].map((item, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3">
                      <item.icon className="w-5 h-5 text-amber-400 flex-shrink-0" />
                      <span className="text-sm text-white/80 font-medium">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Positioning line */}
          <Reveal delay={250}>
            <p className="text-center text-white/40 text-sm mt-12 font-medium">
              StayMii integrates with your PMS. No system change required.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════ SECTION: STAYMII LITE ═══════ */}
      <section className="py-20 bg-slate-50" data-testid="staymii-lite-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 rounded-full text-slate-600 text-sm font-medium mb-4">
                Optional Tool
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-3">
                No PMS? Use StayMii Lite
              </h2>
              <p className="text-slate-500 text-base max-w-2xl mx-auto">
                StayMii Lite is a lightweight tool for operators without an existing system. It keeps you organised and compliant.
              </p>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: CalendarDays, label: 'Manual booking creation' },
                  { icon: BookOpen, label: 'Calendar updates' },
                  { icon: UserCheck, label: 'Basic booking records' },
                  { icon: Receipt, label: 'Integrated e-invoicing' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-9 h-9 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-slate-600" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{item.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 text-center mt-6">
                StayMii Lite is designed for small hotels, homestays, and Airbnb hosts currently managing with spreadsheets or manual processes.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ SECTION: PARTNER ECOSYSTEM ═══════ */}
      <section className="py-20 lg:py-28 bg-white" data-testid="partner-ecosystem-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <SectionTag><Link2 className="h-4 w-4" /> Partner Ecosystem</SectionTag>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Channel Manager & PMS Integrations for e-Invoice
              </h2>
              <p className="text-slate-500 text-base">
                StayMii integrates with widely used PMS and channel management platforms across the hospitality industry.
              </p>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              {[
                { name: 'Beds24', status: 'Live since 23 Oct 2026', statusColor: 'bg-emerald-100 text-emerald-700', logo: beds24Logo },
                { name: 'SiteMinder', status: 'Application In Progress', statusColor: 'bg-amber-100 text-amber-700', logo: siteminderLogo },
                { name: 'Little Hotelier', status: 'Coming Soon', statusColor: 'bg-amber-100 text-amber-700', logo: littleHotelierLogo },
                { name: 'Guesty', status: 'Coming Soon', statusColor: 'bg-amber-100 text-amber-700', logo: guestyLogo },
              ].map((platform, i) => (
                <div key={i} className="bg-white p-5 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all text-center">
                  <div className="w-14 h-14 rounded-xl overflow-hidden mx-auto mb-3 border border-slate-100 bg-white flex items-center justify-center">
                    <img src={platform.logo} alt={`${platform.name} logo`} className="w-full h-full object-contain" />
                  </div>
                  <p className="font-semibold text-slate-900 text-sm mb-2">{platform.name}</p>
                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${platform.statusColor}`}>
                    {platform.status}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={120}>
            <p className="text-center text-sm text-slate-400">
              Don't see your platform? <Link to="/contact" className="text-[#2563EB] hover:underline font-medium">Contact us</Link> — we're adding new integrations regularly.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════ POSITIONING BANNER ═══════ */}
      <section className="py-8 bg-slate-50 border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-500 font-medium text-sm">
            StayMii integrates with your PMS. No system change required.
          </p>
        </div>
      </section>

      {/* ═══════ RELATED e-INVOICE PAGES ═══════ */}
      <section className="py-16 bg-white border-t border-slate-100" data-testid="related-pages-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Learn More About e-Invoice Malaysia</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { to: '/e-invoice-malaysia', label: 'e-Invoice Malaysia Guide', desc: 'Complete guide to LHDN digital invoicing' },
                { to: '/lhdn-e-invoice', label: 'LHDN e-Invoice Compliance', desc: 'Requirements & deadlines for hotels' },
                { to: '/hotel-e-invoice-system', label: 'Hotel e-Invoice System', desc: 'Automated LHDN invoicing for hotels' },
                { to: '/pms-e-invoice-integration', label: 'PMS e-Invoice Integration', desc: 'Connect your PMS to LHDN MyInvois' },
                { to: '/channel-manager-e-invoice', label: 'Channel Manager e-Invoice', desc: 'Automate OTA booking invoices' },
                { to: '/airbnb-e-invoice-malaysia', label: 'Airbnb e-Invoice Malaysia', desc: 'For short-term rental hosts' },
              ].map((link, i) => (
                <Link key={i} to={link.to} className="block p-4 bg-slate-50 hover:bg-blue-50 rounded-xl border border-slate-200 hover:border-blue-200 transition-all group">
                  <p className="font-semibold text-slate-900 group-hover:text-[#2563EB] text-sm mb-1">{link.label}</p>
                  <p className="text-xs text-slate-500">{link.desc}</p>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ CTA SECTION ═══════ */}
      <section className="py-20 lg:py-28 bg-[#0B1120]" data-testid="features-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
              Not sure which system to use?
            </h2>
            <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto">
              Talk to us and we'll recommend the right setup for your property — whether you have an existing PMS or need help getting started.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group px-8 py-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-semibold text-lg hover:shadow-2xl hover:shadow-blue-500/25 transition-all flex items-center justify-center gap-2"
                data-testid="features-cta-consultation-btn"
              >
                Book Consultation <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/einvoice/portal/auth"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-semibold text-lg hover:bg-white/15 transition-all border border-white/20 flex items-center justify-center gap-2"
                data-testid="features-cta-getstarted-btn"
              >
                Get Started <ChevronRight className="h-5 w-5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <HRPromoCard />

      <LandingFooter />
    </div>
  );
};

export default Features;
